import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Sparkles,
  Check,
  Edit3,
  RefreshCw,
  AlertCircle,
  Upload,
} from 'lucide-react';
import { PublishWizard } from './PublishWizard';
import { publishEnrichmentToCatalogue, suggestDomain } from './publishEnrichment';
import { Button } from '../../components/ui/Button';
import {
  EnrichmentDetail,
  EnrichmentSourceProduct,
  EnrichmentSummary,
  ProductEnrichment,
  approveEnrichment,
  getEnrichment,
  listEnrichments,
  regenerateEnrichment,
  rejectEnrichment,
} from '../../services/distributorService';
import { attributesToFeatureStrings } from '../productFeatureSpecs';

/**
 * Master/detail Enrichment review surface — replaces the old single-pane
 * panel + per-row modal flow. Left column is the Draft queue; right column
 * is a side-by-side comparison of the raw Ingram product feed vs the
 * Aitryx-AI-enriched output for the selected SKU.
 *
 * Header carries summary stats (in review · approved · approval rate) and
 * "Reject all" / "Approve all" macros for bulk Draft-clearing.
 *
 * Per-row Approve / Reject / Regenerate buttons live in the detail pane
 * footer. Inline-edit on the AI side is supported via an Edit toggle —
 * partner mutates the AI fields in place and the next Approve persists
 * the edits over the agent output.
 */
interface EnrichmentReviewPanelProps {
  distributorId: string;
  distributorLabel: string;
  /**
   * Optional SKU to pre-select on mount. Set by parent IngramTab when the
   * partner clicks "Edit" inside a Browse pane → the review queue lands
   * with that SKU already highlighted in the master/detail layout.
   * Consumed once, then null-out via onSkuConsumed so re-mounts don't
   * keep yanking selection.
   */
  initialSelectedSku?: string | null;
  onSkuConsumed?: () => void;
  /**
   * Bumped by the parent IngramTab whenever the queue-trigger sync polls
   * detect newly-written enrichment rows. Re-runs the list load so the
   * queue card stays in sync with what the worker is actually committing
   * to SQL — without it the user sees a stale "0 drafts" state for ~10s
   * after every Run sync.
   */
  refreshKey?: number;
}

const BULK_APPROVE_CONCURRENCY = 6;

type Filter = 'Draft' | 'Active' | 'Rejected';

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<void>,
  onProgress: () => void,
): Promise<{ ok: T[]; fail: Array<{ item: T; message: string }> }> {
  const ok: T[] = [];
  const fail: Array<{ item: T; message: string }> = [];
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const idx = cursor++;
      const item = items[idx];
      try { await fn(item); ok.push(item); }
      catch (err: any) { fail.push({ item, message: err?.message ?? 'Unknown error' }); }
      finally { onProgress(); }
    }
  }
  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: workerCount }, worker));
  return { ok, fail };
}

export function EnrichmentReviewPanel({
  distributorId,
  distributorLabel,
  initialSelectedSku,
  onSkuConsumed,
  refreshKey,
}: EnrichmentReviewPanelProps) {
  const [filter, setFilter] = useState<Filter>('Draft');
  const [items, setItems] = useState<EnrichmentSummary[] | null>(null);
  const [allCounts, setAllCounts] = useState<{ active: number; rejected: number } | null>(null);
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [detail, setDetail] = useState<EnrichmentDetail | null>(null);
  const [edited, setEdited] = useState<ProductEnrichment | null>(null);
  const [editing, setEditing] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState<'approve' | 'reject' | 'regenerate' | null>(null);
  const [bulkRunning, setBulkRunning] = useState<{ kind: 'approve' | 'reject'; total: number; done: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmingBulk, setConfirmingBulk] = useState<'approve' | 'reject' | null>(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const [filtered, all] = await Promise.all([
        listEnrichments(distributorId, filter),
        listEnrichments(distributorId).catch(() => null),
      ]);
      setItems(filtered.items);
      // Compute all-time counts for the header stats. Pre-hydration servers
      // returned only Status; new server fills canonical name + confidence
      // inline so the queue cards render rich without an N+1.
      if (all?.items) {
        setAllCounts({
          active: all.items.filter((i) => i.status === 'Active').length,
          rejected: all.items.filter((i) => i.status === 'Rejected').length,
        });
      }
      // Auto-select the first row when nothing is selected, OR when the
      // current selection is no longer in the filtered set.
      setSelectedSku((prev) => {
        if (prev && filtered.items.some((i) => i.sku === prev)) return prev;
        return filtered.items[0]?.sku ?? null;
      });
    } catch (err: any) {
      setError(err.message ?? 'Failed to load enrichments');
    } finally {
      setLoadingList(false);
    }
  }, [distributorId, filter]);

  useEffect(() => {
    loadList();
    // refreshKey participates so the parent's post-sync poll loop forces a
    // fresh listEnrichments call as the worker drains the queue. Same
    // loadList memoisation, just an extra trigger key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, refreshKey]);

  // Honour the initialSelectedSku prop when the panel mounts (or when the
  // parent re-fires it). If the SKU exists under a non-Draft status, switch
  // the filter so the row is visible in the queue. Then call onSkuConsumed
  // so the parent clears its prop and we don't re-yank selection on
  // subsequent renders.
  useEffect(() => {
    if (!initialSelectedSku) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await listEnrichments(distributorId);
        if (cancelled) return;
        const row = all.items.find((i) => i.sku === initialSelectedSku);
        if (!row) {
          // SKU not yet enriched — leave a friendly hint via the error banner.
          setError(`${initialSelectedSku} isn't enriched yet — add it in Catalogue browse and run a sync first.`);
        } else {
          if (row.status !== filter) setFilter(row.status as Filter);
          setSelectedSku(initialSelectedSku);
        }
      } catch {
        // Network glitch — ignore; user can navigate manually.
      } finally {
        if (!cancelled) onSkuConsumed?.();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedSku]);

  // Detail load whenever selection changes.
  useEffect(() => {
    if (!selectedSku) { setDetail(null); setEdited(null); return; }
    let cancelled = false;
    setLoadingDetail(true);
    setEditing(false);
    (async () => {
      try {
        const result = await getEnrichment(distributorId, selectedSku);
        if (cancelled) return;
        setDetail(result);
        setEdited(result.enrichment);
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message ?? 'Failed to load enrichment detail');
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [distributorId, selectedSku]);

  // Per-row actions on the selected detail.
  const handleApprove = async () => {
    if (!edited || !detail) return;
    setBusy('approve');
    setError(null); setSuccess(null);
    try {
      await approveEnrichment(distributorId, detail.sku, editing ? edited : undefined);

      // Auto-publish to the All Products snapshot so the SKU appears in the
      // partner's product list as soon as Approve is clicked. This used to
      // be a manual second step via the Publish Wizard, but partners read
      // "Approve" as "make it real" and the wizard was an unnecessary gate.
      // Publish failure here is non-fatal: the SQL row is already Active,
      // so the partner can still finish via the Publish Wizard if needed.
      // Use sensible defaults — live distributor price with default
      // markup, domain inferred from the enrichment category. The wizard
      // remains accessible as "Edit listing details" for partners who want
      // to tune markup / image URL / name later.
      let publishMessage: string | null = null;
      try {
        const enrichmentForPublish = editing ? edited : (detail.enrichment ?? edited);
        const publishResult = await publishEnrichmentToCatalogue(detail, enrichmentForPublish, {
          source: distributorId as 'ingram',
          sourceLabel: distributorLabel,
          sku: detail.sku,
          domain: suggestDomain(enrichmentForPublish.category?.primary ?? null),
        });
        publishMessage = publishResult.updated
          ? `${detail.sku} approved · listing refreshed in Products.`
          : `${detail.sku} approved · added to Products.`;
      } catch (publishErr: any) {
        publishMessage = `${detail.sku} approved, but couldn't add to Products: ${publishErr?.message ?? 'unknown error'}. Open Edit listing to retry.`;
      }
      setSuccess(publishMessage);

      // Move on to the next item in the queue automatically.
      const next = nextSku(items ?? [], detail.sku);
      setSelectedSku(next);
      await loadList();
    } catch (err: any) {
      setError(err.message ?? 'Approval failed');
    } finally { setBusy(null); }
  };

  /**
   * Approve + publish for the bulk-approve workflow. Each row needs a
   * separate <code>getEnrichment</code> roundtrip to load the priceAndStock
   * + enrichment payload that <code>publishEnrichmentToCatalogue</code>
   * needs (the bulk API only sees SKU summaries). Wrapped in a single
   * Promise so the existing <code>runWithConcurrency</code> helper can
   * fan out at <code>BULK_APPROVE_CONCURRENCY</code>.
   */
  const approveAndPublish = useCallback(async (sku: string): Promise<void> => {
    // Fetch detail first — pay the Ingram round-trip cost only on bulk
    // approve (a deliberate, infrequent action). Same shape used by the
    // single-row Approve path above.
    const detailForSku = await getEnrichment(distributorId, sku);
    await approveEnrichment(distributorId, sku);
    if (!detailForSku.enrichment) return; // No payload — partner can publish manually later.
    try {
      await publishEnrichmentToCatalogue(detailForSku, detailForSku.enrichment, {
        source: distributorId as 'ingram',
        sourceLabel: distributorLabel,
        sku,
        domain: suggestDomain(detailForSku.enrichment.category?.primary ?? null),
      });
    } catch {
      // Swallow publish failures during bulk — Approve already succeeded
      // and we don't want one bad publish to mark the whole row as failed.
      // Partner can revisit and use Edit listing to retry per-SKU.
    }
  }, [distributorId, distributorLabel]);

  const handleReject = async () => {
    if (!detail) return;
    setBusy('reject');
    setError(null); setSuccess(null);
    try {
      await rejectEnrichment(distributorId, detail.sku);
      setSuccess(`${detail.sku} rejected.`);
      const next = nextSku(items ?? [], detail.sku);
      setSelectedSku(next);
      await loadList();
    } catch (err: any) {
      setError(err.message ?? 'Reject failed');
    } finally { setBusy(null); }
  };

  const handleRegenerate = async () => {
    if (!detail) return;
    setBusy('regenerate');
    setError(null); setSuccess(null);
    setEditing(false);
    try {
      await regenerateEnrichment(distributorId, detail.sku);
      // Re-fetch the detail in place.
      const fresh = await getEnrichment(distributorId, detail.sku);
      setDetail(fresh);
      setEdited(fresh.enrichment);
    } catch (err: any) {
      setError(err.message ?? 'Regenerate failed');
    } finally { setBusy(null); }
  };

  const [publishWizardOpen, setPublishWizardOpen] = useState(false);
  const openPublishWizard = () => {
    if (!detail || !edited) return;
    setError(null); setSuccess(null);
    setPublishWizardOpen(true);
  };
  const handlePublishSuccess = ({ sku, updated }: { sku: string; updated: boolean }) => {
    setPublishWizardOpen(false);
    setSuccess(updated ? `${sku} updated in your catalogue.` : `${sku} published to your catalogue.`);
  };
  const handlePublishError = (message: string) => {
    setError(message);
  };

  // Bulk macros — Approve/Reject every Draft in the queue. Confirm modal
  // gates these because they're powerful and irreversible-ish (Reject
  // requires Regenerate to recover; Approve flips Status to Active).
  const runBulk = async (kind: 'approve' | 'reject') => {
    if (!items || items.length === 0) return;
    const skus = items.map((i) => i.sku);
    setBulkRunning({ kind, total: skus.length, done: 0 });
    setError(null); setSuccess(null);
    const concurrency = kind === 'approve' ? BULK_APPROVE_CONCURRENCY : BULK_APPROVE_CONCURRENCY;
    const fn = (sku: string) =>
      kind === 'approve'
        // Bulk approve also auto-publishes — partners expect "Approve all"
        // to drop the rows into Products in one shot, mirroring the per-row
        // Approve UX. Trade-off: one extra Ingram detail fetch per SKU
        // (~300ms) but parallelised at BULK_APPROVE_CONCURRENCY=6.
        ? approveAndPublish(sku)
        : rejectEnrichment(distributorId, sku).then(() => undefined);
    const { ok, fail } = await runWithConcurrency(skus, concurrency, fn, () =>
      setBulkRunning((curr) => (curr ? { ...curr, done: curr.done + 1 } : curr)),
    );
    setBulkRunning(null);
    setConfirmingBulk(null);
    if (fail.length === 0) {
      setSuccess(
        kind === 'approve'
          ? `All ${ok.length} approved and added to Products.`
          : `All ${ok.length} rejected.`,
      );
    } else {
      setError(`${ok.length} ${kind === 'approve' ? 'approved' : 'rejected'}, ${fail.length} failed.`);
    }
    await loadList();
  };

  // Header stats.
  const stats = useMemo(() => {
    const draft = filter === 'Draft' ? (items?.length ?? 0) : null;
    const active = allCounts?.active ?? null;
    const rejected = allCounts?.rejected ?? null;
    const judged = (active ?? 0) + (rejected ?? 0);
    const approvalRate = judged > 0 ? Math.round(((active ?? 0) / judged) * 100) : null;
    return { draft, active, rejected, approvalRate };
  }, [filter, items, allCounts]);

  return (
    <div className="px-6 pt-2 pb-10 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-start gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600 flex-shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Enrichment review</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {filter === 'Draft' && stats.draft !== null
                ? `${stats.draft} AI-enriched SKU${stats.draft === 1 ? '' : 's'} awaiting your approval`
                : `${items?.length ?? 0} ${filter.toLowerCase()} enrichment${items?.length === 1 ? '' : 's'}`}
              {' · '}<span className="font-medium text-slate-700">{distributorLabel}</span>
            </p>
          </div>
        </div>
        {filter === 'Draft' && items && items.length > 0 && (
          <div className="inline-flex items-center gap-2 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmingBulk('reject')}
              disabled={bulkRunning !== null || busy !== null}
              className="inline-flex h-9"
            >
              Reject all
            </Button>
            <Button
              type="button"
              onClick={() => setConfirmingBulk('approve')}
              disabled={bulkRunning !== null || busy !== null}
              className="inline-flex h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="h-4 w-4 mr-1" />
              Approve all
            </Button>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-600 mb-4">
        {stats.draft !== null && (
          <span><span className="font-semibold text-slate-900">{stats.draft}</span> in review</span>
        )}
        {stats.active !== null && (
          <span><span className="font-semibold text-slate-900">{stats.active.toLocaleString()}</span> approved</span>
        )}
        {stats.approvalRate !== null && (
          <span><span className="font-semibold text-slate-900">{stats.approvalRate}%</span> approval rate</span>
        )}
        {/* Filter selector */}
        <div className="inline-flex items-center gap-1 ml-auto text-xs">
          {(['Draft', 'Active', 'Rejected'] as Filter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`inline-flex px-2.5 py-1 rounded-md ${
                filter === s
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Banners */}
      {(error || success || bulkRunning) && (
        <div className="space-y-2 mb-3">
          {bulkRunning && (
            <div className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
              <Loader2 className="h-4 w-4 animate-spin" />
              {bulkRunning.kind === 'approve' ? 'Approving' : 'Rejecting'}{' '}
              {bulkRunning.done}/{bulkRunning.total}…
            </div>
          )}
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
              {success}
            </div>
          )}
        </div>
      )}

      {/* Master / detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Queue */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Queue · {items?.length ?? 0}
          </div>
          {loadingList ? (
            <div className="p-4 text-xs text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading…
            </div>
          ) : !items || items.length === 0 ? (
            <p className="p-4 text-xs text-slate-500">
              No {filter.toLowerCase()} enrichments.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[700px] overflow-y-auto">
              {items.map((item) => (
                <QueueCard
                  key={item.sku}
                  item={item}
                  selected={item.sku === selectedSku}
                  onClick={() => setSelectedSku(item.sku)}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="rounded-lg border border-slate-200 bg-white">
          {loadingDetail ? (
            <div className="p-8 text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading detail…
            </div>
          ) : !detail || !edited ? (
            <div className="p-10 text-center">
              <p className="text-sm text-slate-700 font-medium">
                {items && items.length > 0 ? 'Pick a SKU to review' : `No ${filter.toLowerCase()} enrichments to review`}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {filter === 'Draft' && items && items.length > 0
                  ? 'Click any row in the queue to see the side-by-side AI-vs-raw comparison.'
                  : ''}
              </p>
            </div>
          ) : (
            <DetailPane
              detail={detail}
              edited={edited}
              setEdited={setEdited}
              editing={editing}
              setEditing={setEditing}
              busy={busy}
              filter={filter}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              onOpenPublish={openPublishWizard}
            />
          )}
        </div>
      </div>

      {/* Bulk-confirm modal */}
      {confirmingBulk && items && (
        <BulkConfirmModal
          kind={confirmingBulk}
          count={items.length}
          onCancel={() => setConfirmingBulk(null)}
          onConfirm={() => runBulk(confirmingBulk)}
        />
      )}

      {/* Publish wizard */}
      {publishWizardOpen && detail && edited && (
        <PublishWizard
          open={publishWizardOpen}
          detail={detail}
          enrichment={edited}
          distributorLabel={distributorLabel}
          onClose={() => setPublishWizardOpen(false)}
          onSuccess={handlePublishSuccess}
          onError={handlePublishError}
        />
      )}
    </div>
  );
}

// ─── queue card ────────────────────────────────────────────────────────

function QueueCard({
  item,
  selected,
  onClick,
}: {
  item: EnrichmentSummary;
  selected: boolean;
  onClick: () => void;
}) {
  const conf = parseConfidencePct(item.confidence);
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full text-left p-3 transition-colors relative ${
          selected
            ? 'bg-orange-50 border-l-4 border-orange-500 pl-2'
            : 'hover:bg-slate-50 border-l-4 border-transparent pl-2'
        }`}
      >
        <div className="flex items-start gap-2">
          <VendorBadge vendor={item.vendor ?? null} />
          <span className="font-mono text-xs text-slate-900 flex-1 truncate">{item.sku}</span>
          <span className="text-[10px] text-slate-400 flex-shrink-0">
            {formatRelative(item.enrichedAt)}
          </span>
        </div>
        <div className="text-xs text-slate-700 mt-1.5 line-clamp-2">
          {item.canonicalName ?? <span className="text-slate-400 italic">No name yet</span>}
        </div>
        {conf !== null && (
          <div className="mt-2 grid grid-cols-[1fr_auto] items-center gap-2">
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  conf >= 80 ? 'bg-emerald-500' : conf >= 60 ? 'bg-orange-400' : 'bg-rose-400'
                }`}
                style={{ width: `${conf}%` }}
              />
            </div>
            <span className="text-[11px] text-slate-500 tabular-nums">{conf}%</span>
          </div>
        )}
      </button>
    </li>
  );
}

// ─── detail pane ────────────────────────────────────────────────────────

interface DetailPaneProps {
  detail: EnrichmentDetail;
  edited: ProductEnrichment;
  setEdited: (e: ProductEnrichment) => void;
  editing: boolean;
  setEditing: (b: boolean) => void;
  busy: 'approve' | 'reject' | 'regenerate' | null;
  filter: Filter;
  onApprove: () => void;
  onReject: () => void;
  onRegenerate: () => void;
  onOpenPublish: () => void;
}

function DetailPane({
  detail,
  edited,
  setEdited,
  editing,
  setEditing,
  busy,
  filter,
  onApprove,
  onReject,
  onRegenerate,
  onOpenPublish,
}: DetailPaneProps) {
  const conf = parseConfidencePct(typeof edited.confidence === 'string' ? edited.confidence : null);
  const source = detail.source;
  const isDraft = detail.status === 'Draft';
  const isActive = detail.status === 'Active';

  return (
    <>
      {/* Detail header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            {edited.imageUrl ? (
              <img
                src={edited.imageUrl}
                alt={edited.canonicalName ?? detail.sku}
                className="h-20 w-20 rounded-lg object-contain bg-white border border-slate-200"
              />
            ) : (
              <VendorImagePlaceholder vendor={extractVendor(edited.canonicalName)} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <span className="font-mono text-slate-700">{detail.sku}</span>
              {source?.vendorPartNumber && (
                <>
                  <span>·</span>
                  <span className="font-mono text-slate-500">{source.vendorPartNumber}</span>
                </>
              )}
              <StatusPill status={detail.status} />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mt-1.5 leading-tight">
              {edited.canonicalName ?? detail.sku}
            </h3>
            <div className="mt-2 inline-flex items-center gap-x-4 gap-y-1 text-xs text-slate-600 flex-wrap">
              {(source?.vendor ?? extractVendor(edited.canonicalName)) && (
                <span>
                  <span className="font-medium text-slate-900">
                    {source?.vendor ?? extractVendor(edited.canonicalName)}
                  </span>
                  {edited.category?.primary && (
                    <span className="text-slate-500"> · {edited.category.primary}</span>
                  )}
                </span>
              )}
              {detail.priceAndStock?.price != null && (
                <span className="text-slate-700">
                  <span className="font-semibold text-slate-900 tabular-nums">
                    ${detail.priceAndStock.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-slate-500"> excl. GST</span>
                </span>
              )}
              {detail.priceAndStock?.stock != null && (
                <span className="inline-flex items-center gap-1.5 text-slate-700">
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      detail.priceAndStock.stock > 10
                        ? 'bg-emerald-500'
                        : detail.priceAndStock.stock > 0
                        ? 'bg-amber-500'
                        : 'bg-rose-500'
                    }`}
                  />
                  <span className="font-semibold text-slate-900 tabular-nums">
                    {detail.priceAndStock.stock.toLocaleString()}
                  </span>
                  <span className="text-slate-500">in stock</span>
                </span>
              )}
            </div>
          </div>
          {conf !== null && (
            <div className="flex-shrink-0 text-right">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">AI confidence</div>
              <div
                className={`text-3xl font-semibold tabular-nums mt-0.5 ${
                  conf >= 80 ? 'text-emerald-600' : conf >= 60 ? 'text-orange-500' : 'text-rose-500'
                }`}
              >
                {conf}%
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI status bar — animates from "rewriting" to "complete" on detail load */}
      <AiStatusBar key={detail.sku} confidence={conf} />

      {/* Comparison cards */}
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <RawCard source={source} />
        <EnrichmentCard
          key={`enriched-${detail.sku}`}
          enrichment={edited}
          editing={editing}
          onChange={setEdited}
        />
      </div>

      {/* Actions */}
      <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-2 relative">
        <div className="inline-flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onReject}
            disabled={busy !== null || !isDraft}
            className="inline-flex border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            {busy === 'reject' ? 'Rejecting…' : 'Reject'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onRegenerate}
            disabled={busy !== null}
            className="inline-flex"
            title="Throw this away and ask the agent to try again from scratch"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy === 'regenerate' ? 'animate-spin' : ''}`} />
            {busy === 'regenerate' ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </div>
        <div className="inline-flex items-center gap-2">
          {!editing ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing(true)}
              disabled={busy !== null}
              className="inline-flex"
            >
              <Edit3 className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEdited(detail.enrichment ?? edited);
                setEditing(false);
              }}
              disabled={busy !== null}
              className="inline-flex"
            >
              Discard edits
            </Button>
          )}
          {filter !== 'Active' && (
            <Button
              type="button"
              onClick={onApprove}
              disabled={busy !== null || !isDraft}
              className="inline-flex bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              {busy === 'approve' ? 'Approving…' : editing ? 'Save & approve' : 'Approve'}
            </Button>
          )}
          {isActive && (
            <Button
              type="button"
              onClick={onOpenPublish}
              variant="outline"
              className="inline-flex"
              title="Edit pricing, markup, image URL or display name on the published listing"
            >
              <Upload className="h-3.5 w-3.5 mr-1" />
              Edit listing details
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── comparison cards ────────────────────────────────────────────────────────

function RawCard({ source }: { source?: EnrichmentSourceProduct | null }) {
  if (!source) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="text-sm font-semibold text-slate-900 mb-1">From Ingram</div>
        <p className="text-xs text-slate-500 italic mt-2">
          Raw distributor feed unavailable. Connector may be throttled, or the
          SKU has been removed from the catalogue.
        </p>
      </div>
    );
  }
  const description = source.displayDescription || source.shortDescription || null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm">
      <div>
        <div className="text-sm font-semibold text-slate-900">From Ingram</div>
        <div className="text-[11px] text-slate-500">Raw distributor feed</div>
      </div>
      <Field label="Description" value={description} />
      <Field label="Category" value={source.category?.l1 ?? null} />
      <Field label="Marketing copy" value={null} placeholder="(none provided)" />
      <Field label="Specs" value={extractSpecsFromDescription(description)} />
      <Field label="Inclusions" value={null} placeholder="(none parsed)" />
    </div>
  );
}

function EnrichmentCard({
  enrichment,
  editing,
  onChange,
}: {
  enrichment: ProductEnrichment;
  editing: boolean;
  onChange: (next: ProductEnrichment) => void;
}) {
  const keySpecs = attributesToFeatureStrings(enrichment.attributes ?? {});
  const keySpecsLine = keySpecs.length ? keySpecs.join(' · ') : null;
  const category = enrichment.category?.primary
    ? enrichment.category.secondary
      ? `${enrichment.category.primary} → ${enrichment.category.secondary}`
      : enrichment.category.primary
    : null;

  return (
    <div className="relative rounded-lg border-2 border-orange-200 bg-gradient-to-br from-orange-50/60 to-transparent p-4 space-y-3 text-sm">
      <div>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center shadow-[0_2px_8px_rgba(249,115,22,0.35)]">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-slate-900 inline-flex items-center gap-2">
              Aitryx AI · enriched
              <span className="inline-flex items-center px-2 h-5 rounded text-[10.5px] font-medium border bg-orange-50 text-orange-700 border-orange-200">
                Premium copy
              </span>
            </div>
            <div className="text-[11px] text-slate-600">Ready for your marketplace</div>
          </div>
        </div>
      </div>
      <EditableField
        label="Title"
        value={enrichment.canonicalName}
        editing={editing}
        stream={!editing}
        streamDelay={0}
        onChange={(v) => onChange({ ...enrichment, canonicalName: v })}
      />
      <StreamReveal delay={500}>
        <Field label="Category" value={category} />
      </StreamReveal>
      <EditableField
        label="Marketing copy"
        value={enrichment.marketingSummary}
        editing={editing}
        multiline
        stream={!editing}
        streamDelay={900}
        streamSpeed={11}
        onChange={(v) => onChange({ ...enrichment, marketingSummary: v })}
      />
      <StreamReveal delay={2400}>
        <Field label="Key specs" value={keySpecsLine} />
      </StreamReveal>
      <StreamReveal delay={2900}>
        <Field label="Inclusions" value={null} placeholder="(none parsed)" />
      </StreamReveal>
    </div>
  );
}

// ─── Streaming hooks/components (AI hero treatment) ────────────────────────

/**
 * Types `text` into state character-by-character. Used to make the AI
 * enriched fields feel like they're being written live whenever the user
 * picks a draft from the queue. The effect skips entirely (renders the
 * full text immediately) when `enabled` is false — keeps "Edit" mode and
 * regenerate flows snappy without artificial delays.
 */
function useTypedText(
  fullText: string | null | undefined,
  { speed = 15, delay = 0, enabled = true }: { speed?: number; delay?: number; enabled?: boolean } = {},
): [string, boolean] {
  const safeText = fullText ?? '';
  const [output, setOutput] = useState<string>(enabled ? '' : safeText);
  const [done, setDone] = useState<boolean>(!enabled);

  useEffect(() => {
    if (!enabled) {
      setOutput(safeText);
      setDone(true);
      return;
    }
    setOutput('');
    setDone(false);
    let cancelled = false;
    let i = 0;
    const start = setTimeout(function tick() {
      if (cancelled) return;
      if (i >= safeText.length) {
        setDone(true);
        return;
      }
      const chunk = Math.min(2 + Math.floor(Math.random() * 3), safeText.length - i);
      i += chunk;
      setOutput(safeText.slice(0, i));
      setTimeout(tick, speed + Math.random() * speed);
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(start);
    };
  }, [safeText, enabled, delay, speed]);

  return [output, done];
}

function StreamingValue({
  text,
  delay,
  speed,
  enabled,
}: {
  text: string | null | undefined;
  delay?: number;
  speed?: number;
  enabled: boolean;
}) {
  const [output, done] = useTypedText(text, { delay, speed, enabled });
  const showCursor = enabled && !done && output.length > 0;
  return (
    <span>
      {output}
      {showCursor && (
        <span className="inline-block w-[2px] h-[1em] bg-orange-500 align-text-bottom ml-0.5 animate-pulse" />
      )}
    </span>
  );
}

/**
 * Reveals children after `delay`ms with a small fade/translate. Used for
 * non-text AI fields (category, specs) so they appear in sequence rather
 * than all-at-once after the title finishes typing.
 */
function StreamReveal({ delay, children }: { delay: number; children: React.ReactNode }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    setShown(false);
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div className={`transition-all duration-300 ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}>
      {shown ? children : <div className="h-6 rounded-md bg-orange-100/40 animate-pulse" style={{ width: '60%' }} />}
    </div>
  );
}

/**
 * AI status bar — sits above the raw-vs-AI comparison. Shows a "rewriting"
 * pulse on detail load, then settles to "complete" with confidence after
 * the streaming reveal finishes (~3.5s).
 */
function AiStatusBar({ confidence }: { confidence: number | null }) {
  const [stage, setStage] = useState<'streaming' | 'settled'>('streaming');
  useEffect(() => {
    setStage('streaming');
    const t = setTimeout(() => setStage('settled'), 3500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`border-b border-slate-200 px-5 py-2.5 flex items-center gap-3 transition-colors ${
        stage === 'streaming' ? 'bg-orange-50/50' : 'bg-white'
      }`}
    >
      {stage === 'streaming' ? (
        <>
          <div className="relative w-6 h-6 rounded-md bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center">
            <Sparkles className="h-3 w-3 text-white" />
            <div className="absolute inset-0 rounded-md ring-2 ring-orange-500/30 animate-ping" />
          </div>
          <span className="text-[12.5px] text-slate-800 font-medium">
            Aitryx AI is rewriting the Ingram feed
          </span>
          <span className="text-[11.5px] text-slate-500">· extracting specs · normalising category</span>
        </>
      ) : (
        <>
          <div className="w-6 h-6 rounded-md bg-emerald-50 border border-emerald-200 grid place-items-center">
            <Check className="h-3.5 w-3.5 text-emerald-600" />
          </div>
          <span className="text-[12.5px] text-slate-800 font-medium">Enrichment complete</span>
          {confidence !== null && (
            <span className="text-[11.5px] text-slate-500">
              · confidence <span className="tabular-nums font-medium text-slate-700">{confidence}%</span>
            </span>
          )}
        </>
      )}
      {confidence !== null && (
        <div className="ml-auto flex items-center gap-3">
          <span className="text-[11px] text-slate-500">Confidence</span>
          <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-700"
              style={{ width: stage === 'streaming' ? '50%' : `${confidence}%` }}
            />
          </div>
          <span className="text-[12px] text-slate-800 font-semibold tabular-nums w-9 text-right">
            {stage === 'streaming' ? '…' : `${confidence}%`}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── small components ────────────────────────────────────────────────────────

function Field({
  label,
  value,
  placeholder,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </div>
      <div className="text-sm text-slate-900 whitespace-pre-wrap">
        {value ?? <span className="text-slate-400 italic">{placeholder ?? '—'}</span>}
      </div>
    </div>
  );
}

function EditableField({
  label,
  value,
  editing,
  multiline,
  onChange,
  stream = false,
  streamDelay = 0,
  streamSpeed = 15,
}: {
  label: string;
  value: string | null;
  editing: boolean;
  multiline?: boolean;
  onChange: (v: string | null) => void;
  stream?: boolean;
  streamDelay?: number;
  streamSpeed?: number;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-orange-700 mb-1">
        {label}
      </div>
      {editing ? (
        multiline ? (
          <textarea
            className="w-full rounded-md border border-orange-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            rows={3}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
          />
        ) : (
          <input
            type="text"
            className="w-full rounded-md border border-orange-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
          />
        )
      ) : (
        <div className="text-sm text-slate-900 whitespace-pre-wrap min-h-[1.2em]">
          {value == null || value === '' ? (
            <span className="text-slate-400 italic">—</span>
          ) : stream ? (
            <StreamingValue text={value} delay={streamDelay} speed={streamSpeed} enabled />
          ) : (
            value
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'Active'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : status === 'Rejected'
      ? 'bg-rose-50 text-rose-700 ring-rose-200'
      : 'bg-amber-50 text-amber-700 ring-amber-200';
  const label = status === 'Draft' ? 'In review' : status;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${tone}`}>
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-60" />
      {label}
    </span>
  );
}

function VendorBadge({ vendor }: { vendor: string | null }) {
  const label = (vendor ?? '??').slice(0, 2).toUpperCase();
  const map: Record<string, string> = {
    LE: 'bg-red-500 text-white',
    HP: 'bg-blue-500 text-white',
    DE: 'bg-cyan-600 text-white',
    CI: 'bg-blue-700 text-white',
    FO: 'bg-rose-600 text-white',
    MI: 'bg-emerald-600 text-white',
    AP: 'bg-slate-700 text-white',
    AR: 'bg-orange-500 text-white',
    LO: 'bg-violet-500 text-white',
  };
  const colour = map[label] ?? 'bg-slate-500 text-white';
  return (
    <span className={`inline-flex items-center justify-center h-5 w-5 rounded text-[9px] font-bold flex-shrink-0 ${colour}`}>
      {label}
    </span>
  );
}

function VendorImagePlaceholder({ vendor }: { vendor: string | null }) {
  const label = (vendor ?? '??').slice(0, 2).toUpperCase();
  return (
    <div className="h-20 w-20 rounded-lg bg-blue-500 text-white inline-flex flex-col items-center justify-center font-bold">
      <span className="text-base">{label}</span>
      <span className="text-[9px] opacity-70 mt-0.5">img</span>
    </div>
  );
}

function BulkConfirmModal({
  kind,
  count,
  onCancel,
  onConfirm,
}: {
  kind: 'approve' | 'reject';
  count: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isApprove = kind === 'approve';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0 ${
              isApprove ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}
          >
            {isApprove ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {isApprove ? `Approve all ${count} drafts?` : `Reject all ${count} drafts?`}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {isApprove
                ? 'They\'ll all flip to Active and appear in your catalog. You can still edit individual ones afterwards.'
                : 'They\'ll be suppressed from your catalog. To recover any of them, you\'ll need to Regenerate.'}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="inline-flex">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            className={`inline-flex text-white ${
              isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
            }`}
          >
            {isApprove ? `Approve ${count}` : `Reject ${count}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────

function nextSku(items: EnrichmentSummary[], current: string): string | null {
  const idx = items.findIndex((i) => i.sku === current);
  if (idx === -1) return items[0]?.sku ?? null;
  // Skip the just-actioned item (it'll vanish from the filtered list on reload)
  // and pick the next one OR fall back to the previous.
  return items[idx + 1]?.sku ?? items[idx - 1]?.sku ?? null;
}

function parseConfidencePct(confidence: string | null | undefined): number | null {
  if (!confidence) return null;
  const numericMatch = String(confidence).match(/(\d+)/);
  if (numericMatch) return Math.min(100, parseInt(numericMatch[1], 10));
  switch (confidence.toLowerCase()) {
    case 'high':   return 86;
    case 'medium': return 65;
    case 'low':    return 40;
    default:       return null;
  }
}

function extractVendor(canonicalName: string | null): string | null {
  if (!canonicalName) return null;
  const first = canonicalName.trim().split(/\s+/)[0];
  if (!first) return null;
  const aliases: Record<string, string> = {
    LENOVO: 'Lenovo', HP: 'HP', DELL: 'Dell', CISCO: 'Cisco',
    FORTINET: 'Fortinet', MICROSOFT: 'Microsoft', APPLE: 'Apple',
    ARUBA: 'Aruba', LOGITECH: 'Logitech', APC: 'APC',
  };
  return aliases[first.toUpperCase()] ?? first;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function extractSpecsFromDescription(description: string | null): string | null {
  if (!description) return null;
  // Best-effort: pull the specs-y middle of a typical Ingram description
  // (the whole thing, but trimmed). Real spec extraction lives on the
  // enriched side; raw is informational only.
  return description.length > 200 ? description.slice(0, 200) + '…' : description;
}
